import React from 'react';
import { Helmet } from 'react-helmet-async';
import { UserPlus, CalendarCheck, Star, Globe, Phone, Mail, MapPin } from 'lucide-react';

const PHONE = '403-615-8770';
const PHONE_TEL = '+14036158770';
const EMAIL = 'Radinovicw@shaw.ca';
const WEBSITE = 'https://www.dnbplumbing.ca';
const BUSINESS = 'DNB Plumbing, Heating & Gas Fitting Inc.';
const CONTACT_NAME = 'Mike Radinovic';
const SERVICE_AREA = 'Calgary and surrounding areas';
const REVIEW_URL = 'https://g.page/r/CXoaqTTF7EAnEAE/review';
const REQUEST_URL = 'https://www.dnbplumbing.ca/contact';

const VCARD_TEXT = `BEGIN:VCARD
VERSION:3.0
N:Radinovic;Mike;;;
FN:${CONTACT_NAME}
ORG:${BUSINESS}
TITLE:Owner & Lead Tradesman | Journeyman Plumber & Gas Fitter
TEL;TYPE=CELL,VOICE:${PHONE_TEL}
EMAIL;TYPE=INTERNET:${EMAIL}
URL:${WEBSITE}
ADR;TYPE=WORK:;;;Calgary;AB;;Canada
NOTE:Service Area: ${SERVICE_AREA}
END:VCARD
`;

const CardPage = () => {
  const handleAddContact = () => {
    const blob = new Blob([VCARD_TEXT], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'dnb-plumbing-mike-radinovic.vcf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <main
      data-testid="card-page"
      className="min-h-screen w-full font-body bg-white flex items-start justify-center px-4 py-10 sm:py-14"
    >
      <Helmet>
        <title>DNB Plumbing — Quick Links</title>
        <meta name="description" content="Quick links for DNB Plumbing, Heating & Gas Fitting customers. Save contact, request service, leave a review." />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="w-full max-w-md">
        {/* Card header */}
        <div className="text-center mb-8">
          <div className="inline-block mb-5">
            <img
              src="/dnb_final_header_logo_transparent.png"
              alt="DNB Plumbing, Heating & Gas Fitting Inc."
              className="h-20 sm:h-24 w-auto mx-auto"
            />
          </div>
          <h1 className="font-heading font-extrabold text-3xl text-[#0F172A] leading-tight mb-2">
            DNB Plumbing
          </h1>
          <p className="text-[#0284C7] text-xs font-bold uppercase tracking-[0.18em] mb-4">
            Quick Links
          </p>
          <p className="text-slate-600 text-sm leading-relaxed">
            Need service, want to save our contact, or leave a review?
            Choose an option below.
          </p>
        </div>

        {/* Quick details row */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-6 shadow-sm shadow-slate-200/40 space-y-2.5 text-sm">
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="card-phone"
            className="flex items-center gap-3 text-[#0F172A] hover:text-[#0284C7] transition-colors"
          >
            <Phone className="w-4 h-4 text-[#0284C7]" />
            <span className="font-semibold">{PHONE}</span>
          </a>
          <a
            href={`mailto:${EMAIL}`}
            data-testid="card-email"
            className="flex items-center gap-3 text-[#0F172A] hover:text-[#0284C7] transition-colors"
          >
            <Mail className="w-4 h-4 text-[#0284C7]" />
            <span className="break-all">{EMAIL}</span>
          </a>
          <div className="flex items-center gap-3 text-slate-600">
            <MapPin className="w-4 h-4 text-[#0284C7]" />
            <span>{SERVICE_AREA}</span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="space-y-3">
          {/* Add Contact */}
          <button
            type="button"
            onClick={handleAddContact}
            data-testid="card-add-contact-btn"
            className="w-full bg-gradient-to-r from-[#38BDF8] to-[#0284C7] hover:from-[#0EA5E9] hover:to-[#0369A1] text-white font-bold rounded-2xl py-5 px-5 shadow-lg shadow-slate-300/40 active:scale-[0.98] transition-all flex items-center gap-4"
          >
            <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
              <UserPlus className="w-6 h-6" strokeWidth={2.2} />
            </span>
            <span className="flex-1 text-left">
              <span className="block font-heading font-extrabold text-base leading-tight">
                Add Contact
              </span>
              <span className="block text-sky-50/90 text-xs font-medium mt-0.5">
                Save Mike's vCard to your phone
              </span>
            </span>
          </button>

          {/* Request Service */}
          <a
            href={REQUEST_URL}
            data-testid="card-request-service-btn"
            className="w-full bg-white border-2 border-[#0284C7] text-[#0F172A] hover:bg-slate-50 font-bold rounded-2xl py-5 px-5 shadow-md shadow-slate-200/60 active:scale-[0.98] transition-all flex items-center gap-4"
          >
            <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-slate-100 text-[#0284C7] flex-shrink-0">
              <CalendarCheck className="w-6 h-6" strokeWidth={2.2} />
            </span>
            <span className="flex-1 text-left">
              <span className="block font-heading font-extrabold text-base leading-tight">
                Request Service
              </span>
              <span className="block text-slate-500 text-xs font-medium mt-0.5">
                Book plumbing, heating, or gas work
              </span>
            </span>
          </a>

          {/* Leave a Review */}
          <a
            href={REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="card-review-btn"
            className="w-full bg-white border-2 border-amber-300 text-[#0F172A] hover:bg-amber-50 font-bold rounded-2xl py-5 px-5 shadow-md shadow-amber-100/60 active:scale-[0.98] transition-all flex items-center gap-4"
          >
            <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-amber-100 text-amber-500 flex-shrink-0">
              <Star className="w-6 h-6" strokeWidth={2.2} fill="currentColor" />
            </span>
            <span className="flex-1 text-left">
              <span className="block font-heading font-extrabold text-base leading-tight">
                Leave a Review
              </span>
              <span className="block text-slate-500 text-xs font-medium mt-0.5">
                Share your experience on Google
              </span>
            </span>
          </a>

          {/* Continue to Site */}
          <a
            href={WEBSITE}
            data-testid="card-continue-site-btn"
            className="w-full bg-[#0F172A] hover:bg-[#075985] text-white font-bold rounded-2xl py-5 px-5 shadow-lg shadow-slate-300/40 active:scale-[0.98] transition-all flex items-center gap-4"
          >
            <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-white/15 flex-shrink-0">
              <Globe className="w-6 h-6" strokeWidth={2.2} />
            </span>
            <span className="flex-1 text-left">
              <span className="block font-heading font-extrabold text-base leading-tight">
                Continue to Site
              </span>
              <span className="block text-slate-300/85 text-xs font-medium mt-0.5">
                Visit dnbplumbing.ca
              </span>
            </span>
          </a>
        </div>

        {/* Tiny footer note */}
        <p className="text-center text-slate-400 text-[11px] mt-8 leading-relaxed">
          {BUSINESS}
          <br />
          Owner-operated &middot; Calgary &amp; surrounding areas
        </p>
      </div>
    </main>
  );
};

export default CardPage;
